import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date'
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj)
}

export function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`

  return formatDate(date)
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36)
}

/**
 * Converts a name stored in any casing to proper Title Case.
 * Handles ALL-CAPS ("SHIPRA ARYA" → "Shipra Arya"), all-lower,
 * and already-correct names without changing them.
 * Preserves Mc/Mac prefixes, hyphenated names, and apostrophes.
 */
export function formatName(name: string | null | undefined): string {
  if (!name) return ''
  return name
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bMc(\w)/g, (_, c) => `Mc${c.toUpperCase()}`)
    .replace(/\bMac(\w)/g, (_, c) => `Mac${c.toUpperCase()}`)
    .replace(/(\w)'(\w)/g, (_, a, b) => `${a}'${b.toUpperCase()}`)
    .trim()
}

/** Returns just the first name in proper case. */
export function firstName(name: string | null | undefined): string {
  return formatName(name).split(' ')[0] ?? ''
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function getChakraColor(chakraIndex: number): string {
  const colors = [
    'text-red-600',     // Root
    'text-orange-500',  // Sacral
    'text-yellow-500',  // Solar Plexus
    'text-green-500',   // Heart
    'text-blue-500',    // Throat
    'text-indigo-600',  // Third Eye
    'text-purple-600'   // Crown
  ]
  return colors[chakraIndex] || 'text-gray-600'
}

export function getChakraName(index: number): string {
  const names = [
    'Root Chakra (Muladhara)',
    'Sacral Chakra (Svadhisthana)',
    'Solar Plexus Chakra (Manipura)',
    'Heart Chakra (Anahata)',
    'Throat Chakra (Vishuddha)',
    'Third Eye Chakra (Ajna)',
    'Crown Chakra (Sahasrara)'
  ]
  return names[index] || 'Unknown Chakra'
}

export function getChakraSymbol(index: number): string {
  const symbols = ['🟥', '🟧', '🟨', '🟩', '🟦', '🟣', '🟪']
  return symbols[index] || '⚪'
}